import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import api, { formatDateISO, formatTimePretty } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Calendar as CalIcon, Clock } from "@phosphor-icons/react";

const STEPS = ["Service", "Date & Time", "Details", "Confirmed"];

export default function Booking() {
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [services, setServices] = useState([]);
    const [serviceId, setServiceId] = useState(null);
    const [date, setDate] = useState(null);
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [time, setTime] = useState(null);
    const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!date) return;
        setSlotsLoading(true);
        setTime(null);
        const iso = formatDateISO(date);
        api.get(`/slots/available?date=${iso}`)
            .then((r) => setSlots(r.data.slots || []))
            .catch(() => setSlots([]))
            .finally(() => setSlotsLoading(false));
    }, [date]);

    const selectedService = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);

    const isWeekday = (d) => {
        const wd = d.getDay();
        return wd >= 1 && wd <= 5;
    };

    const submit = async () => {
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Name and phone are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/appointments", {
                customer_name: form.name.trim(),
                customer_phone: form.phone.trim(),
                customer_email: form.email.trim() || null,
                service_id: serviceId,
                appointment_date: formatDateISO(date),
                appointment_time: time,
                notes: form.notes.trim(),
            });
            setResult(data);
            setStep(3);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Booking failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bone text-ink">
            <SiteHeader active="book" />
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-16">
                {/* Stepper */}
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-ash mb-8" data-testid="booking-stepper">
                    {STEPS.map((s, i) => (
                        <div key={s} className="flex items-center gap-3">
                            <span className={i === step ? "text-moss font-semibold" : i < step ? "text-ink" : ""}>
                                {String(i + 1).padStart(2, "0")} · {s}
                            </span>
                            {i < STEPS.length - 1 && <span className="text-divider">/</span>}
                        </div>
                    ))}
                </div>

                {/* Step 0: Service */}
                {step === 0 && (
                    <div className="animate-fade-up" data-testid="step-service">
                        <h1 className="font-serif text-4xl md:text-5xl font-light mb-3">Choose a service.</h1>
                        <p className="text-ash mb-10">Each session is 30 minutes.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-divider">
                            {services.map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => {
                                        setServiceId(s.id);
                                        setStep(1);
                                    }}
                                    className={`service-card text-left border-r border-b border-divider p-8 ${
                                        serviceId === s.id ? "bg-stone border-moss" : "bg-bone"
                                    }`}
                                    data-testid={`service-option-${s.id}`}
                                >
                                    <div className="font-mono text-xs text-moss">0{services.indexOf(s) + 1}</div>
                                    <div className="font-serif text-2xl mt-3">{s.name}</div>
                                    <div className="text-sm text-ash mt-2">{s.description}</div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-ash mt-6">{s.duration} min</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 1: Date & Time */}
                {step === 1 && (
                    <div className="animate-fade-up" data-testid="step-datetime">
                        <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 text-sm text-ash hover:text-ink mb-6" data-testid="back-to-service">
                            <ArrowLeft size={14} /> Back
                        </button>
                        <h1 className="font-serif text-4xl md:text-5xl font-light mb-3">Pick a date &amp; time.</h1>
                        <p className="text-ash mb-10">Appointments available Mon–Fri, 12:00–17:00.</p>
                        <div className="grid md:grid-cols-2 gap-10">
                            <div>
                                <div className="text-xs uppercase tracking-[0.25em] text-ash mb-4 flex items-center gap-2">
                                    <CalIcon size={14} /> Select date
                                </div>
                                <div className="border border-divider bg-bone p-2 inline-block" data-testid="calendar-wrapper">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={(d) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const max = new Date();
                                            max.setDate(max.getDate() + 60);
                                            return d < today || d > max || !isWeekday(d);
                                        }}
                                        classNames={{
                                            day_selected: "bg-moss text-bone hover:bg-moss-hover",
                                            day_today: "bg-stone text-ink",
                                        }}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.25em] text-ash mb-4 flex items-center gap-2">
                                    <Clock size={14} /> Select time
                                </div>
                                {!date ? (
                                    <div className="text-sm text-ash border border-dashed border-divider p-8 text-center">
                                        Choose a date first.
                                    </div>
                                ) : slotsLoading ? (
                                    <div className="text-sm text-ash">Loading…</div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-2" data-testid="slot-grid">
                                        {slots.map((s) => (
                                            <button
                                                key={s.time}
                                                disabled={!s.available}
                                                onClick={() => setTime(s.time)}
                                                className={`slot-btn px-3 py-3 font-mono text-sm border ${
                                                    time === s.time
                                                        ? "bg-moss text-bone border-moss"
                                                        : s.available
                                                        ? "bg-bone border-divider text-ink"
                                                        : "bg-stone border-divider text-ash line-through cursor-not-allowed"
                                                }`}
                                                data-testid={`slot-${s.time}`}
                                            >
                                                {formatTimePretty(s.time)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <button
                                    onClick={() => setStep(2)}
                                    disabled={!date || !time}
                                    className="mt-8 w-full md:w-auto bg-moss text-bone px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-moss-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="continue-to-details"
                                >
                                    Continue →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Details */}
                {step === 2 && (
                    <div className="animate-fade-up max-w-2xl" data-testid="step-details">
                        <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm text-ash hover:text-ink mb-6" data-testid="back-to-datetime">
                            <ArrowLeft size={14} /> Back
                        </button>
                        <h1 className="font-serif text-4xl md:text-5xl font-light mb-3">Your details.</h1>
                        <p className="text-ash mb-10">
                            {selectedService?.name} · {date && new Date(date).toDateString()} · {time && formatTimePretty(time)}
                        </p>
                        <div className="space-y-6">
                            <Field label="Full name" required>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-bone border border-divider rounded-none px-4 py-3 focus:outline-none focus:border-moss focus:ring-1 focus:ring-moss"
                                    data-testid="input-name"
                                />
                            </Field>
                            <Field label="Phone number" required>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    className="w-full bg-bone border border-divider rounded-none px-4 py-3 focus:outline-none focus:border-moss focus:ring-1 focus:ring-moss font-mono"
                                    data-testid="input-phone"
                                />
                            </Field>
                            <Field label="Email (for confirmation)">
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-bone border border-divider rounded-none px-4 py-3 focus:outline-none focus:border-moss focus:ring-1 focus:ring-moss"
                                    data-testid="input-email"
                                />
                            </Field>
                            <Field label="Notes (optional)">
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={3}
                                    className="w-full bg-bone border border-divider rounded-none px-4 py-3 focus:outline-none focus:border-moss focus:ring-1 focus:ring-moss"
                                    data-testid="input-notes"
                                />
                            </Field>
                            <button
                                onClick={submit}
                                disabled={submitting}
                                className="bg-moss text-bone px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-moss-hover transition-colors disabled:opacity-50"
                                data-testid="submit-booking"
                            >
                                {submitting ? "Booking…" : "Confirm booking"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Confirmed */}
                {step === 3 && result && (
                    <div className="animate-fade-up max-w-2xl text-center mx-auto" data-testid="step-confirmed">
                        <CheckCircle size={64} weight="light" className="text-moss mx-auto mb-6" />
                        <h1 className="font-serif text-4xl md:text-5xl font-light mb-4">You're booked.</h1>
                        <p className="text-ash mb-10">
                            A confirmation {result.customer_email ? "has been sent to your email." : "is shown below."}
                        </p>
                        <div className="border border-divider p-8 bg-stone/30 text-left">
                            <Row label="Reference" value={result.id.slice(0, 8).toUpperCase()} mono />
                            <Row label="Service" value={result.service_name} />
                            <Row label="When" value={`${result.appointment_date} · ${formatTimePretty(result.appointment_time)}`} />
                            <Row label="Name" value={result.customer_name} />
                            <Row label="Phone" value={result.customer_phone} mono />
                        </div>
                        <div className="mt-10 flex gap-4 justify-center">
                            <Link to="/" className="border border-ink px-6 py-3 hover:bg-ink hover:text-bone transition-colors text-sm uppercase tracking-[0.2em]" data-testid="done-home">
                                Done
                            </Link>
                            <button
                                onClick={() => {
                                    setStep(0); setServiceId(null); setDate(null); setTime(null);
                                    setForm({ name: "", phone: "", email: "", notes: "" }); setResult(null);
                                }}
                                className="bg-moss text-bone px-6 py-3 hover:bg-moss-hover text-sm uppercase tracking-[0.2em]"
                                data-testid="book-another"
                            >
                                Book another
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <div className="text-xs uppercase tracking-[0.2em] text-ash mb-2">
                {label} {required && <span className="text-terracotta">*</span>}
            </div>
            {children}
        </label>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="grid grid-cols-3 gap-4 py-2 border-b border-divider last:border-0">
            <div className="text-xs uppercase tracking-[0.2em] text-ash">{label}</div>
            <div className={`col-span-2 ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}
